"use client";

import React from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import FadeInView from "@/components/animations/FadeInView";
import InlineContactForm from "@/components/InlineContactForm";

export default function CtaSection() {
  return (
    <section
      className="relative overflow-hidden py-12 sm:py-16"
      style={{
        background: "#070B14",
      }}
    >
      {/* Radial blue gradient bloom centered behind content */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 70% at 50% 50%, rgba(37,99,235,0.22) 0%, rgba(29,78,216,0.12) 35%, transparent 70%)",
        }}
      />
      {/* Soft ambient blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(37,99,235,0.18) 0%, transparent 70%)",
            filter: "blur(90px)",
          }}
        />
        <div
          className="absolute bottom-[-10%] right-[-5%] w-[450px] h-[450px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(34,211,238,0.1) 0%, transparent 70%)",
            filter: "blur(70px)",
          }}
        />
      </div>
      {/* Animated mesh/grid overlay — matches hero */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(59,130,246,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.06) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "radial-gradient(ellipse 90% 80% at 50% 50%, black 30%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 90% 80% at 50% 50%, black 30%, transparent 100%)",
        }}
      />
      {/* Content — two-column layout */}
      <div className="relative z-10 max-w-container mx-auto px-4 sm:px-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

          {/* Left column: CTA content */}
          <FadeInView y={28} duration={0.6} scale={0.98} delay={0}>
            <div className="text-center lg:text-left">

              {/* Eyebrow badge */}
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-pill mb-6"
                style={{
                  border: "1px solid rgba(59,130,246,0.35)",
                  background: "rgba(37,99,235,0.12)",
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: "#3B82F6" }}
                />
                <span
                  className="text-[11px] font-heading font-600 tracking-widest uppercase gradient-text"
                >
                  Free Infrastructure Assessment
                </span>
              </div>

              {/* Headline */}
              <h2
                className="font-heading font-700 leading-tight mb-5"
                style={{ fontSize: "clamp(28px, 4vw, 48px)", color: "#F8FAFC" }}
              >
                Planning a new site,<br />
                multi-location{" "}
                <span className="gradient-text">rollout, or infrastructure upgrade?</span>
              </h2>

              <p
                className="text-[15px] sm:text-[16px] font-body leading-relaxed mb-8 max-w-[500px] mx-auto lg:mx-0"
                style={{ color: "rgba(203,213,225,0.7)" }}
              >
                Talk to Pro IT Solutions about building secure, scalable technology environments
                designed for real-world operations.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3 mb-8">
                {/* Primary CTA */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                  className="relative"
                >
                  <Link
                    href="/book"
                    className="relative inline-flex items-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-600 btn-primary"
                  >
                    Book a Strategy Call
                    <motion.svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      whileHover={{ x: 3 }}
                      transition={{ duration: 0.22, ease: "easeOut" }}
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </motion.svg>
                  </Link>
                </motion.div>

                {/* Secondary CTA */}
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                  className="relative"
                >
                  <motion.div
                    className="absolute inset-0 rounded-full pointer-events-none"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                    style={{
                      boxShadow: "0 0 16px 4px rgba(34,211,238,0.35)",
                    }}
                  />
                  <Link
                    href="/contact"
                    className="relative inline-flex items-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-500 btn-secondary"
                  >
                    Request a Quote
                  </Link>
                </motion.div>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-4 sm:gap-x-7 gap-y-2 mb-8">
                {[
                  "No lock-in contracts",
                  "Response within 2 hours",
                  "100% Satisfaction Guarantee",
                  "NZ-based team",
                ]?.map((item) => (
                  <span
                    key={item}
                    className="flex items-center gap-1.5 text-[12px] font-body"
                    style={{ color: "rgba(148,163,184,0.65)" }}
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#22D3EE"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {item}
                  </span>
                ))}
              </div>

            </div>
          </FadeInView>

          {/* Right column: Contact form */}
          <FadeInView y={28} duration={0.6} scale={0.98} delay={0.15}>
            <InlineContactForm />
          </FadeInView>

        </div>

        {/* Phone — full width, centered below both columns */}
        <div
          className="mt-10 pt-6 text-center"
          style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
        >
          <p
            className="text-[13px] font-body mb-1.5"
            style={{ color: "rgba(148,163,184,0.5)" }}
          >
            Prefer to call? We&apos;re ready.
          </p>
          <a
            href="tel:0800776481"
            className="font-heading font-700 text-[22px] transition-colors duration-200"
            style={{ color: "#60A5FA" }}
          >
            0800 77 6481
          </a>
        </div>
      </div>
    </section>
  );
}